/**
 * Cloudflare Turnstile lazy loader.
 *
 * The self-service signup screen is pre-login and public, so the backend
 * verifies a Turnstile token before arming the session for OAuth. The widget
 * script is loaded on demand (same shape as `lib/google-picker.ts`) so the
 * rest of the SPA never pays for it.
 */

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loadingPromise: Promise<TurnstileApi> | null = null;

function apiOrReject(): Promise<TurnstileApi> {
  return window.turnstile
    ? Promise.resolve(window.turnstile)
    : Promise.reject(
        new Error('Turnstile script loaded without window.turnstile')
      );
}

/** Resolve the Turnstile API, injecting the script tag on first use. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const settle = () => apiOrReject().then(resolve, reject);
    const fail = () => {
      loadingPromise = null;
      reject(new Error(`Failed to load script: ${TURNSTILE_SCRIPT_URL}`));
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_URL}"]`
    );
    if (existing) {
      existing.addEventListener('load', settle, { once: true });
      existing.addEventListener('error', fail, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', settle, { once: true });
    script.addEventListener('error', fail, { once: true });
    try {
      document.head.appendChild(script);
    } catch {
      fail();
    }
  });
  return loadingPromise;
}
